import { useState } from 'react'

import { Checkbox } from '~/shadcn/ui/form/checkbox'
import { Spinner } from '~/shadcn/ui/spinner'
import { Dialog, DialogContent } from '~/shadcn/ui/dialog'

import { ArrowForwardIcon } from '~/@mid_ui/icons/ArrowForwardIcon'
import { TextTooltip } from '~/@mid_ui/text-tooltip/TextTooltip'
import { MainAgreementPreview } from '../main-agreement-preview/MainAgreementPreview'
import { useMainAgreement } from './useMainAgreement'
import { Button } from '~/shadcn/ui/button/button'
import { Text } from '~/@mid_ui/typography/Text'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  sophisticatedInvestorAnalysisFormSchema,
  type SophisticatedInvestorAnalysisFormSchema,
} from './sophisticatedInvestorAnalysisFormSchema'
import { Form, FormField, FormItem } from '~/shadcn/ui/form/form'
import { Field, FieldError, FieldGroup, FieldLabel } from '~/shadcn/ui/field'
import { cn } from '~/shadcn/lib/utils'

type Props = { isSigningAgreement: boolean; onSign(): void }

export function SophisticatedAgreementForm({
  isSigningAgreement,
  onSign,
}: Props) {
  const {
    agreement,
    error: agreementLoadError,
    isLoading: isLoadingMainAgreement,
  } = useMainAgreement()

  const form = useForm<SophisticatedInvestorAnalysisFormSchema>({
    resolver: zodResolver(sophisticatedInvestorAnalysisFormSchema),
    defaultValues: {
      isInfoTrueChecked: false,
      isAgreedToTermsChecked: false,
      isAwareOfInvestorRisksChecked: false,
    },
  })

  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false)

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSign)}>
          <FieldGroup>
            <FormField
              name="isInfoTrueChecked"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem className="gap-1">
                  <FieldLabel className="flex flex-row items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isSigningAgreement}
                    />
                    <Text className="text-sm font-medium leading-normal">
                      Všetky uvedené informácie sú pravdivé.
                    </Text>
                  </FieldLabel>
                  {fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />

            <FormField
              name="isAwareOfInvestorRisksChecked"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem className="gap-1">
                  <FieldLabel className="flex flex-row items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isSigningAgreement}
                    />
                    <Text className="text-sm font-medium leading-normal">
                      Som si vedomý dosledkov straty ochrany investora spojenej
                      s postavením nesofistikovaného investora.
                      <TextTooltip
                        text={
                          'Vzhľadom k tomu, že ste sofistikovaný investor, uvedomujete si riziká investovania a nie je potrebné prejsť simuláciou ohľadne schopnosti znášať stratu. Simulácia je súbor otázok ohľadne výšky vašich aktív, príjmov a závazkov.'
                        }
                      />
                    </Text>
                  </FieldLabel>
                  {fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />

            <FormField
              name="isAgreedToTermsChecked"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem className="gap-1">
                  <FieldLabel className="flex flex-row items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isSigningAgreement}
                    />
                    <Text className="text-sm font-medium leading-normal">
                      Súhlasím s podmienkami{' '}
                      <Button
                        variant="link"
                        size="xs"
                        className="text-accent p-0 h-1"
                        onClick={(event) => {
                          event.preventDefault()
                          setIsAgreementModalOpen(true)
                        }}>
                        rámcovej zmluvy
                      </Button>{' '}
                      o investovaní na platforme Invest in Slovakia.
                    </Text>
                  </FieldLabel>
                  {fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />
            <Field>
              <Text className="text-sm font-medium leading-normal">
                Na vašu e-mailovú adresu sme zaslali dokument Informačné
                povinnosti dodávateľa.
              </Text>
            </Field>
            <Field>
              <Button
                variant="accent"
                type="submit"
                disabled={
                  isSigningAgreement ||
                  isLoadingMainAgreement ||
                  agreement == null
                }
                size="2xl">
                Potvrdiť a podpísať{' '}
                {isSigningAgreement ? (
                  <Spinner className="size-6" />
                ) : (
                  <ArrowForwardIcon />
                )}
              </Button>
            </Field>
          </FieldGroup>
        </form>
      </Form>
      <Dialog
        open={isAgreementModalOpen}
        onOpenChange={(isOpen) => setIsAgreementModalOpen(isOpen)}>
        <DialogContent
          className={cn(
            'flex flex-col max-w-[min(42rem,calc(100%-2rem))] h-[calc(100svh-4rem)] md:py-0 md:px-0',
            'text-[13px] font-semibold text-muted-foreground'
          )}>
          <MainAgreementPreview
            agreement={agreement}
            error={agreementLoadError}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
